/* eslint-disable react-refresh/only-export-components */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode, } from 'react';
import { authLogin, authRegister, authLogout, authMe, storeTokens, normalizeAuthResponse } from '@/services/authService';
import { joinActivity, leaveActivity, deleteActivity } from '@/services/activityService';
import type { ApiUser } from '@/lib/types';
import type { ActivityTypeNum, HelpTypeNum } from '@/lib/types';
import { useQueryClient } from '@tanstack/react-query';
import { activityKeys } from './activities/useActivities';
import { formatLocalDateTime } from '@/lib/dateTime';

// ---- Type compatible avec le reste du front (nom + email + id) ----
export interface User {
  id: string;
  name: string;
  email: string;
  emailConfirmed: boolean;
  avatarUrl?: string | null;
  isAdmin?: boolean;
  isModerator?: boolean;

}

// ---- Type minimal Activity pour ne pas tout casser ----
export interface Activity {
  id: string;
  type: ActivityTypeNum;
  helpType?: HelpTypeNum;
  title: string;
  description: string;
  organizer: string;
  time: string;
  endsAt?: string | null;
  nextSessionStartsAt?: string | null;
  upcomingSessionsCount?: number;
  timeCategory: 'TODAY' | 'WEEK' | 'UPCOMING';
  distanceKm: number;
  location: string;
  participants: number;
  maxParticipants: number | null;
  tags: string[];
  isOwner?: boolean;
  commentsCount?: number;
  createdAt: string;
}

interface AuthResult {
  ok: boolean;
  message?: string;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  signup: (name: string, email: string, password: string, isAtLeast17: boolean) => Promise<AuthResult>;
  logout: () => Promise<void>;
  joinedIds: string[];
  isJoined: (id: string) => boolean;
  toggleJoin: (id: string, joinedOverride?: boolean) => Promise<void>;
  createdActivities: Activity[];
  addCreatedActivity: (activity: Activity) => void;
  removeCreatedActivity: (id: string) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function apiUserToUser(apiUser: ApiUser): User {
  const roles = (apiUser.roles ?? []).map((r) => r.toLowerCase());
  const hasRole = (name: string) =>
    roles.some((r) => r === name || r === `role.${name}` || r === `roles.${name}`);
  const isAdmin = apiUser.isAdmin ?? hasRole('admin');
  const isModerator = apiUser.isModerator ?? hasRole('moderator');
  return {
    id: apiUser.id,
    name: apiUser.displayName ?? apiUser.name ?? apiUser.email ?? 'Utilisateur',
    email: apiUser.email,
    emailConfirmed: apiUser.emailConfirmed ?? false,
    avatarUrl: apiUser.avatarUrl ?? null,
    isAdmin,
    isModerator,
  };
}

const JOINED_KEY = 'adp_joined_v2';

function loadJoined(): string[] {
  try {
    const raw = localStorage.getItem(JOINED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function saveJoined(ids: string[]) {
  try {
    localStorage.setItem(JOINED_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [joinedIds, setJoinedIds] = useState<string[]>(loadJoined);
  const [createdActivities, setCreatedActivities] = useState<Activity[]>([]);

  const queryClient = useQueryClient();
  // Restaure la session au démarrage, avec refresh automatique si l'access token mémoire a disparu.
  useEffect(() => {
    authMe()
      .then((apiUser) => setUser(apiUserToUser(apiUser)))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Sync joinedIds to localStorage
  useEffect(() => {
    saveJoined(joinedIds);
  }, [joinedIds]);

  const login = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !password) {
      return { ok: false, message: 'Renseigne ton email et ton mot de passe.' };
    }
    try {
      const raw = await authLogin({ email: normalized, password });
      const res = normalizeAuthResponse(raw);
      storeTokens({ accessToken: res.accessToken });
      setUser(apiUserToUser(res.user));
      return { ok: true };
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Email ou mot de passe incorrect.';

      if (msg.startsWith('ACCOUNT_BANNED_UNTIL|')) {
        const bannedUntil = msg.split('|')[1];

        return {
          ok: false,
          message: `Compte suspendu jusqu’au ${formatLocalDateTime(bannedUntil)}.`,
        };
      }

      return { ok: false, message: msg };
    }
  }, []);

  const signup = useCallback(async (name: string, email: string, password: string, isAtLeast17: boolean): Promise<AuthResult> => {
    const normalized = email.trim().toLowerCase();
    if (!name.trim() || !normalized || password.length < 8) {
      return {
        ok: false,
        message: 'Complète tous les champs (mot de passe de 8 caractères minimum).',
      };
    }
    try {
      const raw = await authRegister({ email: normalized, displayName: name.trim(), password, isAtLeast17 });
      const res = normalizeAuthResponse(raw);
      storeTokens({ accessToken: res.accessToken });
      setUser(apiUserToUser(res.user));
      return { ok: true };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Impossible de créer le compte.';
      return { ok: false, message: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    await authLogout().catch(() => { });
    setUser(null);
    setJoinedIds([]);
    setCreatedActivities([]);
  }, []);


  const refreshUser = useCallback(async () => {
    try {
      const apiUser = await authMe();
      setUser(apiUserToUser(apiUser));
    } catch {
      // ignore
    }
  }, []);



  const isJoined = useCallback((id: string) => joinedIds.includes(id), [joinedIds]);

  const toggleJoin = useCallback(
    async (id: string, joinedOverride?: boolean) => {
      const alreadyJoined =
        joinedOverride ?? joinedIds.includes(id);

      setJoinedIds((prev) =>
        alreadyJoined
          ? prev.filter((x) => x !== id)
          : prev.includes(id)
            ? prev
            : [...prev, id]
      );

      try {
        if (alreadyJoined) {
          await leaveActivity(id);
        } else {
          await joinActivity(id);
        }

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: activityKeys.all,
          }),

          queryClient.invalidateQueries({
            queryKey: ['home-feed'],
          }),
        ]);
      } catch {
        setJoinedIds((prev) =>
          alreadyJoined
            ? prev.includes(id)
              ? prev
              : [...prev, id]
            : prev.filter((x) => x !== id)
        );

        throw new Error(
          alreadyJoined
            ? "Impossible de quitter l'activité"
            : "Impossible de rejoindre l'activité"
        );
      }
    },
    [joinedIds, queryClient]
  );
  // Ajouter une activité
  const addCreatedActivity = useCallback((activity: Activity) => {
    setCreatedActivities((prev) => [activity, ...prev]);
  }, []);

  const removeCreatedActivity = useCallback(async (id: string) => {
    setCreatedActivities((prev) => prev.filter((a) => a.id !== id));
    try {
      await deleteActivity(id);
    } catch {
      // ignore if already removed
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      signup,
      logout,
      joinedIds,
      isJoined,
      toggleJoin,
      createdActivities,
      addCreatedActivity,
      removeCreatedActivity,
      refreshUser,
    }),
    [
      user,
      loading,
      login,
      signup,
      logout,
      joinedIds,
      isJoined,
      toggleJoin,
      createdActivities,
      addCreatedActivity,
      removeCreatedActivity,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return ctx;
}